/**
 * Tenant Usage Panel
 * 
 * Displays detailed usage information for a specific tenant including:
 * - Storage usage with progress bar
 * - Agents quota usage
 * - Members quota usage
 * - Collections quota usage
 */

import { Skeleton } from '@/components/ui';
import { Progress } from '@/components/ui/progress';
import { useTenantUsage } from '../hooks';
import { HardDrive, Bot, Users, FolderOpen, FileText, Coins } from 'lucide-react';

interface TenantUsagePanelProps {
  tenantId: string;
  /** Monthly token budget from tenant config. null = unlimited. */
  maxTokensMonth: number | null;
}

function UsageSkeleton() {
  return (
    <div className="space-y-4">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="space-y-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-2 w-full" />
        </div>
      ))}
    </div>
  );
}

interface UsageItemProps {
  icon: React.ReactNode;
  label: string;
  used: number;
  quota: number;
  unit?: string;
  format?: (value: number) => string;
}

function UsageItem({ icon, label, used, quota, unit = '', format }: UsageItemProps) {
  const percent = quota > 0 ? (used / quota) * 100 : 0;
  const isCritical = percent > 90;
  const isWarning = percent > 75;
  
  const formatValue = format ?? ((v: number) => v.toString());

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-medium">
          {icon}
          {label}
        </div>
        <span className={`text-sm ${isCritical ? 'text-destructive' : isWarning ? 'text-amber-600' : 'text-muted-foreground'}`}>
          {formatValue(used)}{unit} / {formatValue(quota)}{unit}
        </span>
      </div>
      <Progress 
        value={percent}
        className={isCritical ? '[&>div]:bg-destructive' : isWarning ? '[&>div]:bg-amber-500' : ''}
      />
    </div>
  );
}

export function TenantUsagePanel({ tenantId, maxTokensMonth }: TenantUsagePanelProps) {
  const { data: usage, isLoading, error } = useTenantUsage(tenantId);

  if (isLoading) {
    return <UsageSkeleton />;
  }

  if (error) {
    return (
      <div className="text-sm text-destructive">
        Failed to load usage data
      </div>
    );
  }

  if (!usage) {
    return null;
  }

  return (
    <div className="space-y-4">
      <UsageItem
        icon={<HardDrive className="h-4 w-4 text-muted-foreground" />}
        label="Storage"
        used={usage.storage_used_gb}
        quota={usage.storage_quota_gb}
        unit=" GB"
        format={(v) => v.toFixed(1)}
      />

      <UsageItem
        icon={<Bot className="h-4 w-4 text-muted-foreground" />}
        label="Agents"
        used={usage.agents_used}
        quota={usage.agents_quota}
      />

      <UsageItem
        icon={<Users className="h-4 w-4 text-muted-foreground" />}
        label="Members"
        used={usage.members_used}
        quota={usage.members_quota}
      />

      <UsageItem
        icon={<FolderOpen className="h-4 w-4 text-muted-foreground" />}
        label="Collections"
        used={usage.collections_used}
        quota={usage.collections_quota}
      />

      <div className="pt-2 border-t">
        <div className="flex items-center gap-2 text-sm">
          <FileText className="h-4 w-4 text-muted-foreground" />
          <span className="text-muted-foreground">Total Documents:</span>
          <span className="font-medium">{usage.documents_total.toLocaleString()}</span>
        </div>
      </div>

      {/* Token budget row — only shown when a limit is configured and usage is tracked */}
      {maxTokensMonth != null && maxTokensMonth > 0 && usage.tokens_used_month != null && (
        <UsageItem
          icon={<Coins className="h-4 w-4 text-muted-foreground" />}
          label="Tokens (this month)"
          used={usage.tokens_used_month}
          quota={maxTokensMonth}
          format={(v) => (v / 1_000_000).toFixed(2) + 'M'}
        />
      )}
    </div>
  );
}
